import { SORT_ICON_ASC, SORT_ICON_DESC } from '@appViews/shared/sortIcon';
import useCollectionOptions from '@boHooks/useCollectionOptions';
import useTests from '@boHooks/useTests';
import TestService from '@boServices/testService';
import { languageName, uiLocale } from '@boViews/shared/LanguageTabs/languageName';
import TestCreatePanel from '@boViews/shared/TestCreatePanel';
import { faEraser, faPlus } from '@fortawesome/pro-regular-svg-icons';
import ActiveFiltersBar from '@sharedComponents/ActiveFiltersBar';
import Button from '@sharedComponents/Button';
import FlexBox from '@sharedComponents/FlexBox';
import MultiSelectFilter from '@sharedComponents/MultiSelectFilter';
import Pagination from '@sharedComponents/Pagination';
import Panel from '@sharedComponents/Panel';
import SearchBar from '@sharedComponents/SearchBar';
import Sidebar from '@sharedComponents/Sidebar';
import Table from '@sharedComponents/Table';
import { showToast } from '@sharedComponents/Toast';
import Typography from '@sharedComponents/Typography';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { getTestColumns } from './TestRow';

const testService = new TestService();

const PAGE_SIZE = 20;
// Table renders a (possibly empty) sort button on every header; only these columns are sortable server-side.
const SORTABLE_COLUMNS = ['reference', 'title', 'level', 'status', 'collectionsCount'];

const TestList = () => {
    const { t, i18n } = useTranslation('bo');
    const locale = uiLocale(i18n);
    const navigate = useNavigate();
    const searchBarRef = useRef();

    const [search, setSearch] = useState('');
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [selectedLanguageIds, setSelectedLanguageIds] = useState([]);
    const [selectedLevelIds, setSelectedLevelIds] = useState([]);
    const [page, setPage] = useState(1);
    const [sortBy, setSortBy] = useState('reference');
    const [createOpen, setCreateOpen] = useState(false);

    const { tests, total, loading, error } = useTests({
        search,
        page,
        perPage: PAGE_SIZE,
        statuses: selectedStatuses,
        languageIds: selectedLanguageIds,
        levelIds: selectedLevelIds,
        sortBy,
    });
    const { languagesOptions, levelsOptions, loading: optionsLoading } = useCollectionOptions();

    const totalPages = Math.ceil(total / PAGE_SIZE) || 1;

    const statusOptions = [
        { value: 'DRAFT', label: t('Draft') },
        { value: 'ONLINE', label: t('Online') },
        { value: 'ARCHIVED', label: t('Archived') },
    ];
    const languageOptions = useMemo(
        () => languagesOptions.map((option) => ({ value: option.value, label: languageName(option.label, locale) })),
        [languagesOptions, locale],
    );

    const sortIcon = sortBy.startsWith('-') ? SORT_ICON_DESC : SORT_ICON_ASC;

    const handleSort = (column) => {
        if (!SORTABLE_COLUMNS.includes(column)) return;
        setSortBy((old) => {
            const oldIdDesc = old.startsWith('-');
            if (old.includes(column)) {
                return oldIdDesc ? column : `-${column}`;
            } else {
                return oldIdDesc ? `-${column}` : column;
            }
        });
        setPage(1);
    };

    const hasActiveFilters =
        search.trim().length > 0 || selectedStatuses.length > 0 || selectedLanguageIds.length > 0 || selectedLevelIds.length > 0;
    const failed = !loading && Boolean(error);
    const noResults = !loading && !failed && tests.length === 0;

    // Rejects on failure so that the panel can map a 409 onto its reference field.
    const handleCreate = async (payload) => {
        try {
            const created = await testService.createTest(payload);
            showToast({ type: 'success', text: t('The test has been created.') });
            navigate(`/tests/${encodeURIComponent(created.reference)}`);
        } catch (err) {
            if (err.status !== 409) {
                showToast({ type: 'error', text: t('An error occurred while creating the test. Please try again.') });
            }
            throw err;
        }
    };

    // onTextChange rather than the options flow: it ignores the react-select blur,
    // which used to clear the input without resetting the query, and it also reports
    // the empty string when the field is cleared.
    const handleSearch = useCallback((text) => {
        setSearch(text);
        setPage(1);
    }, []);

    const handleStatusChange = (values) => {
        setSelectedStatuses(values);
        setPage(1);
    };

    const handleLanguageChange = (ids) => {
        setSelectedLanguageIds(ids);
        setPage(1);
    };

    const handleLevelChange = (ids) => {
        setSelectedLevelIds(ids);
        setPage(1);
    };

    const clearSearch = () => {
        // SearchBar is uncontrolled: clearing the query does not empty the visible input.
        searchBarRef.current?.setInputValue('');
        setSearch('');
    };

    const handleReset = () => {
        clearSearch();
        setSelectedStatuses([]);
        setSelectedLanguageIds([]);
        setSelectedLevelIds([]);
        setPage(1);
    };

    // Chips of the active filters, in the order of the filters, each one removing its own value.
    const activeFilters = [
        ...statusOptions
            .filter((option) => selectedStatuses.includes(option.value))
            .map((option) => ({
                key: `status-${option.value}`,
                label: option.label,
                remove: () => setSelectedStatuses((values) => values.filter((value) => value !== option.value)),
            })),
        ...languageOptions
            .filter((option) => selectedLanguageIds.includes(option.value))
            .map((option) => ({
                key: `language-${option.value}`,
                label: option.label,
                remove: () => setSelectedLanguageIds((ids) => ids.filter((id) => id !== option.value)),
            })),
        ...levelsOptions
            .filter((option) => selectedLevelIds.includes(option.value))
            .map((option) => ({
                key: `level-${option.value}`,
                label: option.label,
                remove: () => setSelectedLevelIds((ids) => ids.filter((id) => id !== option.value)),
            })),
        ...(search.trim() ? [{ key: 'search', label: `"${search.trim()}"`, remove: clearSearch }] : []),
    ];

    const handleRemoveFilter = (key) => {
        activeFilters.find((filter) => filter.key === key)?.remove();
        setPage(1);
    };

    return (
        <FlexBox flexDirection="column" gap="l" additionnalStyles={{ padding: 'var(--space-l) var(--space-xl)' }}>
            <FlexBox justifyContent="space-between" alignItems="center">
                <Typography fontSize="xl" fontWeight="bold" color="branded-primary" seo={1}>
                    {t('Tests')}
                </Typography>
                <Button icon={faPlus} label={t('New test')} action={() => setCreateOpen(true)} disabled={optionsLoading} />
            </FlexBox>

            <Panel>
                <FlexBox justifyContent="flex-end" alignItems="center" gap="s" additionnalStyles={{ padding: 'var(--space-m)' }}>
                    <MultiSelectFilter label={t('Status')} options={statusOptions} value={selectedStatuses} onChange={handleStatusChange} />
                    <MultiSelectFilter
                        label={t('Language')}
                        options={languageOptions}
                        value={selectedLanguageIds}
                        onChange={handleLanguageChange}
                    />
                    <MultiSelectFilter label={t('Level')} options={levelsOptions} value={selectedLevelIds} onChange={handleLevelChange} />
                    <FlexBox additionnalStyles={{ width: '320px' }}>
                        <SearchBar
                            ref={searchBarRef}
                            hideMenu
                            compact
                            minChars={0}
                            debounceMs={300}
                            controlShouldRenderValue={false}
                            search={async () => []}
                            onTextChange={handleSearch}
                            onClear={() => handleSearch('')}
                            placeholder={t('Search a test…')}
                        />
                    </FlexBox>
                </FlexBox>

                <ActiveFiltersBar color="lavender" filters={activeFilters} onRemove={handleRemoveFilter} onReset={handleReset} />

                {failed ? (
                    <FlexBox justifyContent="center" additionnalStyles={{ padding: 'var(--space-xl)' }}>
                        <Typography color="error-primary">{t('The tests could not be loaded. Please try again.')}</Typography>
                    </FlexBox>
                ) : noResults ? (
                    <FlexBox flexDirection="column" gap="m" alignItems="center" additionnalStyles={{ padding: 'var(--space-xl)' }}>
                        <Typography color="neutral-secondary">{t('No tests found.')}</Typography>
                        {hasActiveFilters && <Button ghost icon={faEraser} label={t('Reset filters')} action={handleReset} />}
                    </FlexBox>
                ) : (
                    <Table
                        columns={getTestColumns(t, {
                            onOpen: (test) => navigate(`/tests/${encodeURIComponent(test.reference)}`),
                            sortBy,
                            sortIcon,
                            languagesOptions,
                            locale,
                        })}
                        items={tests}
                        loading={loading}
                        borderless
                        getRowKey={(test) => test.reference}
                        onSort={handleSort}
                    />
                )}

                {!noResults && !failed && (
                    <FlexBox
                        justifyContent="space-between"
                        alignItems="center"
                        additionnalStyles={{
                            borderTop: '1px solid var(--border-color-neutral-tertiary)',
                            padding: 'var(--space-xs) var(--space-m) var(--space-s)',
                        }}
                    >
                        <Typography fontSize="xs" color="neutral-secondary">
                            {total} {t('results')}
                        </Typography>
                        <Pagination totalPages={totalPages} selectedPage={page} onChange={setPage} />
                    </FlexBox>
                )}
            </Panel>

            <Sidebar isOpened={createOpen} onClose={() => setCreateOpen(false)} title={t('New test')}>
                {createOpen && (
                    <TestCreatePanel standalone languagesOptions={languagesOptions} levelsOptions={levelsOptions} onSubmit={handleCreate} />
                )}
            </Sidebar>
        </FlexBox>
    );
};

export default TestList;
